import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { translations, Lang, Dict } from '../i18n/translations';

type Theme = 'light' | 'dark';

interface Settings {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Dict;
  theme: Theme;
  toggleTheme: () => void;
}

const SettingsContext = createContext<Settings | null>(null);

const LANG_KEY = 'ds-lang';
const THEME_KEY = 'ds-theme';

// Document titles per language (default language is Dutch).
const titles: Record<Lang, string> = {
  nl: 'DS-Innovation | Websites voor kleine ondernemingen',
  en: 'DS-Innovation | Websites for small businesses',
};

function readLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'nl') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'nl'; // default: Dutch
}

function readTheme(): Theme {
  try {
    // Only honour an explicit, stored choice; otherwise default to light.
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'light'; // default: light
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readLang);
  const [theme, setThemeState] = useState<Theme>(readTheme);

  // Apply language: html lang attribute + document title + persist.
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    document.title = titles[lang];
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  // Apply theme: data-ds-theme attribute + theme-color meta + persist.
  // NB: we use `data-ds-theme` (not `data-theme`) because Chakra UI's color
  // mode manager owns `data-theme` and would otherwise overwrite ours.
  useEffect(() => {
    document.documentElement.setAttribute('data-ds-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0e1116' : '#14b8a6');
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);
  const toggleTheme = useCallback(() => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')), []);

  const value = useMemo<Settings>(
    () => ({ lang, setLang, t: translations[lang], theme, toggleTheme }),
    [lang, setLang, theme, toggleTheme]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export function useSettings(): Settings {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
}
